import type { TFunction } from "i18next"
import {
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Copy,
  EllipsisVertical,
  ImageIcon,
  Images,
  Layers,
  Library,
  MapPin,
  Package,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  Users,
  Video,
  Volume2,
  Waypoints,
  type LucideIcon,
} from "lucide-react"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import { createPortal } from "react-dom"
import { PostProductionIcon } from "@/components/icons/post-production-icon"
import type { CastRow, LocationRow, PropRow } from "@/components/prompt-editor/mention-sources"
import { HighlightedText } from "@/components/ui/highlighted-text"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { useFlowMenuPins } from "@/hooks/use-flow-menu-pins"
import { useTranslation } from "@/hooks/use-translation"
import { characterListThumbnail } from "@/lib/character-thumbnail"
import { fetchFlowShotTree } from "@/lib/flow-api"
import { locationListThumbnail } from "@/lib/location-thumbnail"
import { cn } from "@/lib/utils"
import type { CanvasShotTreeEpisode, CanvasShotTreeScene, CanvasShotTreeShot } from "@/types/canvas"
import type { FlowChainRow, FlowNodeKind } from "@/types/flow"
import { flowChainConnects, flowFreshKindConnects, type FlowConnectDrag } from "@/types/flow-connectors"

export type FlowMenuConnectFrom = FlowConnectDrag & {
  nodeId: number
}

export type FlowMenuState = {
  x: number
  y: number
  worldX: number
  worldY: number
  nodeId: number | null
  connectFrom?: FlowMenuConnectFrom | null
}

export type FlowMenuEntity = {
  id: number
  name: string
  imageUrl?: string | null
}

type FlowContextMenuProps = {
  menu: FlowMenuState
  projectId: number
  canEdit: boolean
  characters: CastRow[]
  locations: LocationRow[]
  props: PropRow[]
  onClose: () => void
  onUpload: () => void
  onLibrary: () => void
  onImage: () => void
  onVideo: () => void
  onVideoTransition: () => void
  onAudio: () => void
  onUpscale: () => void
  onDepth: () => void
  onShot: (shotId: number) => void
  onNewShot: (sceneId: number) => void
  onCharacter: (characterId: number) => void
  onLocation: (locationId: number) => void
  onProp: (propId: number) => void
  onDelete: () => void
  chains: FlowChainRow[]
  onChain: (chainId: number) => void
  onEditChain: (chainId: number) => void
  onDuplicateChain: (chainId: number) => void
  onDeleteChain: (chainId: number) => void
  /** Цепочка ноды, по которой открыто меню. `null` — пункт «Редактировать» не нужен. */
  editChainId: number | null
  canCreateChain: boolean
  onCreateChain: () => void
  canRegenerate: boolean
  onRegenerate: () => void
  /** Пункт «Создать цепочку» в секции цепочек. Нет колбэка — пункта нет. */
  onCreateBlankChain?: () => void
}

type FlowT = TFunction<"flow">

type MenuKind =
  | "image"
  | "video"
  | "video_transition"
  | "video_upscale"
  | "video_depth"
  | "upload"
  | "audio"
  | "library"
type MenuGroup = "gen" | "media" | "project"
type MenuFilter = "all" | MenuGroup | "chains"
type EntityView = "characters" | "locations" | "props"

type MenuView =
  | { name: "root" }
  | { name: "pick" }
  | { name: "episodes" }
  | { name: "scenes"; episodeId: number }
  | { name: "shots"; episodeId: number; sceneId: number }
  | { name: EntityView }

type MenuAction =
  | { type: "kind"; kind: MenuKind }
  | { type: "open"; view: MenuView }
  | { type: "entity"; view: EntityView; id: number }
  | { type: "shot"; id: number }
  | { type: "newShot"; sceneId: number }
  | { type: "chain"; id: number }
  | { type: "createBlankChain" }

type MenuEntry = {
  key: string
  label: string
  sub?: string
  icon?: LucideIcon
  thumb?: { url: string | null; wide: boolean }
  section?: string
  action: MenuAction
  pinKey?: string
  edit?: { label: string; onSelect: () => void }
  /** Строка цепочки: булавка и правка живут в меню «⋯», а не отдельными кнопками. */
  chainId?: number
}

type ShotRef = { episode: CanvasShotTreeEpisode; scene: CanvasShotTreeScene; shot: CanvasShotTreeShot }

type MenuData = {
  t: FlowT
  episodes: CanvasShotTreeEpisode[]
  shots: ShotRef[]
  entities: Record<EntityView, FlowMenuEntity[]>
  connectDrag: FlowConnectDrag | null
  pins: string[]
  chains: FlowChainRow[]
  onCreateBlankChain?: () => void
}

const MENU_WIDTH = 340
const MENU_MAX_HEIGHT = 440
const NODE_MENU_WIDTH = 208
const NODE_MENU_ROW = 40
const SEARCH_LIMIT = 40
const FILTERS: MenuFilter[] = ["all", "gen", "media", "project", "chains"]
const ENTITY_VIEWS: EntityView[] = ["characters", "locations", "props"]

const KIND_ITEMS = [
  { kind: "image", group: "gen", icon: ImageIcon, label: "menu.image", sub: "menu.subs.generate" },
  { kind: "video", group: "gen", icon: Video, label: "menu.video", sub: "menu.subs.generate" },
  {
    kind: "video_transition",
    group: "gen",
    icon: Images,
    label: "menu.videoTransition",
    sub: "menu.subs.videoTransition",
  },
  {
    kind: "video_upscale",
    group: "gen",
    icon: PostProductionIcon,
    label: "menu.upscale",
    sub: "menu.subs.postProduction",
  },
  {
    kind: "video_depth",
    group: "gen",
    icon: Layers,
    label: "menu.depth",
    sub: "menu.subs.depth",
  },
  { kind: "upload", group: "media", icon: Upload, label: "menu.upload", sub: "menu.subs.upload" },
  { kind: "audio", group: "media", icon: Volume2, label: "menu.audio", sub: "menu.subs.audio" },
  { kind: "library", group: "media", icon: Library, label: "menu.library", sub: "menu.subs.library" },
] as const satisfies readonly { kind: MenuKind; group: MenuGroup; icon: LucideIcon; label: string; sub: string }[]

type KindItem = (typeof KIND_ITEMS)[number]

const ENTITY_META = {
  characters: {
    icon: Users,
    label: "menu.character",
    empty: "menu.noCharacters",
    count: "menu.counts.characters",
    pin: "character",
  },
  locations: {
    icon: MapPin,
    label: "menu.location",
    empty: "menu.noLocations",
    count: "menu.counts.locations",
    pin: "location",
  },
  props: { icon: Package, label: "menu.prop", empty: "menu.noProps", count: "menu.counts.props", pin: "prop" },
} as const

export function FlowContextMenu(props: FlowContextMenuProps) {
  const {
    menu,
    canEdit,
    onClose,
    onDelete,
    canCreateChain,
    onCreateChain,
    canRegenerate,
    onRegenerate,
    editChainId,
    onEditChain,
  } = props
  const { t } = useTranslation("flow")

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) onClose()
    }
    const onPointer = () => onClose()
    window.addEventListener("keydown", onKey)
    window.addEventListener("pointerdown", onPointer)
    return () => {
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("pointerdown", onPointer)
    }
  }, [onClose])

  if (!canEdit) return null

  if (menu.nodeId != null) {
    const rows = 1 + (editChainId != null ? 1 : 0) + (canCreateChain ? 1 : 0) + (canRegenerate ? 1 : 0)
    return (
      <MenuShell x={menu.x} y={menu.y} width={NODE_MENU_WIDTH} maxHeight={8 + rows * NODE_MENU_ROW}>
        <div className="p-1">
          {editChainId != null ? (
            <NodeMenuButton icon={Pencil} label={t("menu.edit")} onClick={() => onEditChain(editChainId)} />
          ) : null}
          {canRegenerate ? (
            <NodeMenuButton icon={Sparkles} label={t("menu.regenerate")} onClick={onRegenerate} />
          ) : null}
          {canCreateChain ? (
            <NodeMenuButton icon={Waypoints} label={t("menu.createChain")} onClick={onCreateChain} />
          ) : null}
          <NodeMenuButton icon={Trash2} label={t("menu.delete")} danger onClick={onDelete} />
        </div>
      </MenuShell>
    )
  }

  return <AddNodePalette {...props} />
}

function AddNodePalette({
  menu,
  projectId,
  characters,
  locations,
  props: propItems,
  onUpload,
  onLibrary,
  onImage,
  onVideo,
  onVideoTransition,
  onAudio,
  onUpscale,
  onDepth,
  onShot,
  onNewShot,
  onCharacter,
  onLocation,
  onProp,
  chains,
  onChain,
  onEditChain,
  onDuplicateChain,
  onDeleteChain,
  onCreateBlankChain,
}: FlowContextMenuProps) {
  const { t } = useTranslation("flow")
  const connectDrag = menu.connectFrom ?? null
  const needsShots = connectDrag == null || flowFreshKindConnects(connectDrag, "shot")
  const [view, setView] = useState<MenuView>({ name: "root" })
  const [direction, setDirection] = useState<"none" | "forward" | "back">("none")
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<MenuFilter>("all")
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [justPinned, setJustPinned] = useState<string | null>(null)
  const [chainMenuKey, setChainMenuKey] = useState<string | null>(null)
  const [tree, setTree] = useState<CanvasShotTreeEpisode[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [pins, togglePin] = useFlowMenuPins(projectId)
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const filtersRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!needsShots) return
    let cancelled = false
    void fetchFlowShotTree(projectId)
      .then((next) => {
        if (!cancelled) setTree(next)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
    return () => {
      cancelled = true
    }
  }, [needsShots, projectId])

  const episodes = useMemo(() => (tree ?? []).filter((episode) => episode.scenes.length > 0), [tree])
  const entities = useMemo(
    () => ({
      characters: characters.map((item) => ({ id: item.id, name: item.name, imageUrl: characterListThumbnail(item) })),
      locations: locations.map((item) => ({ id: item.id, name: item.name, imageUrl: locationListThumbnail(item) })),
      props: propItems.map((item) => ({ id: item.id, name: item.name, imageUrl: propMenuThumbUrl(item) })),
    }),
    [characters, locations, propItems],
  )
  const resolved = resolveView(view, episodes)
  const viewKey = menuViewKey(resolved)
  const inRoot = resolved.name === "root"
  const showFilters = inRoot
  const words = useMemo(() => (query.trim() ? [query.trim()] : []), [query])

  const entries = useMemo(() => {
    const data: MenuData = {
      t,
      episodes,
      shots: flattenShots(episodes),
      entities,
      connectDrag,
      pins,
      chains,
      onCreateBlankChain,
    }
    return menuEntries(data, resolved, query, filter)
  }, [t, episodes, entities, connectDrag, pins, chains, onCreateBlankChain, resolved, query, filter])

  const resetKey = `${viewKey}|${query}|${filter}`
  const glide = useGlide(listRef, activeKey, `${resetKey}|${entries.length}|${pins.join(",")}`, viewKey)
  const filterGlide = useGlide(filtersRef, showFilters ? filter : null, "filters", "filters")

  useEffect(() => {
    if (chainMenuKey && !entries.some((entry) => entry.key === chainMenuKey)) setChainMenuKey(null)
  }, [entries, chainMenuKey])

  useEffect(() => {
    setActiveKey(entries[0]?.key ?? null)
    setJustPinned(null)
    setChainMenuKey(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- первая строка выбирается при смене вида, запроса или фильтра, а не при каждом пересчёте списка
  }, [resetKey])

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true })
  }, [viewKey])

  useEffect(() => {
    const scroller = scrollRef.current
    const row = activeKey ? findMenuRow(listRef.current, activeKey) : null
    if (!scroller || !row) return
    const top = row.offsetTop
    const bottom = top + row.offsetHeight
    if (top < scroller.scrollTop) scroller.scrollTop = top - 4
    else if (bottom > scroller.scrollTop + scroller.clientHeight)
      scroller.scrollTop = bottom - scroller.clientHeight + 4
  }, [activeKey])

  const kindHandlers: Record<MenuKind, () => void> = {
    image: onImage,
    video: onVideo,
    video_transition: onVideoTransition,
    video_upscale: onUpscale,
    video_depth: onDepth,
    upload: onUpload,
    audio: onAudio,
    library: onLibrary,
  }
  const entityHandlers: Record<EntityView, (id: number) => void> = {
    characters: onCharacter,
    locations: onLocation,
    props: onProp,
  }

  const go = (next: MenuView) => {
    setDirection("forward")
    setView(next)
    setQuery("")
  }

  const goBack = () => {
    setDirection("back")
    setQuery("")
    if (resolved.name === "shots") setView({ name: "scenes", episodeId: resolved.episodeId })
    else if (resolved.name === "scenes" && episodes.length > 1) setView({ name: "episodes" })
    else setView({ name: "root" })
  }

  const run = (entry: MenuEntry) => {
    setChainMenuKey(null)
    const action = entry.action
    if (action.type === "open") go(action.view)
    else if (action.type === "kind") kindHandlers[action.kind]()
    else if (action.type === "entity") entityHandlers[action.view](action.id)
    else if (action.type === "shot") onShot(action.id)
    else if (action.type === "chain") onChain(action.id)
    else if (action.type === "createBlankChain") onCreateBlankChain?.()
    else onNewShot(action.sceneId)
  }

  const pin = (key: string) => {
    setJustPinned(pins.includes(key) ? null : key)
    togglePin(key)
    if (chainMenuKey == null) inputRef.current?.focus({ preventScroll: true })
  }

  const activeEntry = entries.find((entry) => entry.key === activeKey) ?? null
  const note = menuNote(t, resolved, {
    loading: needsShots && tree == null && !loadError,
    loadError,
    noScenes: tree != null && episodes.length === 0,
    query,
    empty: entries.length === 0,
    noChains: filter === "chains",
  })
  const placeholder = !inRoot ? t("menu.searchIn", { title: viewTitle(t, resolved, episodes) }) : t("menu.search")

  return (
    <MenuShell x={menu.x} y={menu.y} width={MENU_WIDTH} maxHeight={MENU_MAX_HEIGHT}>
      <div className="flex h-11.5 items-center gap-2 border-b border-ui-border px-3.5">
        <Search className="size-4 shrink-0 text-ui-text-muted" />
        {!inRoot ? (
          <button
            type="button"
            className="inline-flex h-5.5 max-w-36 shrink-0 items-center gap-1 rounded-sm bg-ui-surface-raised pr-1.5 pl-1 text-xs text-ui-text-secondary transition-colors hover:bg-ui-surface-hover"
            onClick={goBack}
          >
            <ChevronLeft className="size-3 shrink-0" />
            <span className="truncate">{viewTitle(t, resolved, episodes)}</span>
          </button>
        ) : null}
        <input
          ref={inputRef}
          value={query}
          placeholder={placeholder}
          aria-label={placeholder}
          className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] text-ui-text-primary shadow-none outline-none placeholder:text-ui-text-muted focus:ring-0 focus:outline-none"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (chainMenuKey && (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter")) {
              event.preventDefault()
              return
            }
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault()
              setActiveKey(moveActive(entries, activeKey, event.key === "ArrowDown" ? 1 : -1))
            } else if (event.key === "Enter" && activeEntry) {
              event.preventDefault()
              run(activeEntry)
            } else if (event.key === "ArrowRight" && query === "" && activeEntry?.action.type === "open") {
              event.preventDefault()
              run(activeEntry)
            } else if ((event.key === "Backspace" || event.key === "ArrowLeft") && query === "" && !inRoot) {
              event.preventDefault()
              goBack()
            } else if (event.key === "Escape" && chainMenuKey) {
              event.preventDefault()
              setChainMenuKey(null)
            } else if (event.key === "Escape" && query !== "") {
              event.preventDefault()
              setQuery("")
            } else if (event.key === "Tab" && showFilters) {
              event.preventDefault()
              const step = event.shiftKey ? -1 : 1
              setFilter(FILTERS[(FILTERS.indexOf(filter) + step + FILTERS.length) % FILTERS.length])
            }
          }}
        />
      </div>
      {showFilters ? (
        <MenuFilterRow
          scrollerRef={filtersRef}
          glide={filterGlide}
          filter={filter}
          onSelect={(item) => {
            setFilter(item)
            inputRef.current?.focus({ preventScroll: true })
          }}
        />
      ) : null}
      <AutoHeight>
        <div ref={scrollRef} className="it2tv-scrollbar max-h-80 overflow-y-auto">
          <div
            key={viewKey}
            ref={listRef}
            role="listbox"
            className={cn(
              "relative p-1",
              direction === "forward" && "flow-menu-view-forward",
              direction === "back" && "flow-menu-view-back",
            )}
          >
            <GlideHighlight glide={glide} className="rounded-sm bg-ui-surface-raised" />
            {note ? <div className="px-3 py-5 text-center text-sm text-ui-text-muted">{note}</div> : null}
            <MenuRows
              entries={entries}
              activeKey={activeKey}
              words={words}
              pins={pins}
              justPinned={justPinned}
              pinLabel={t("menu.pin")}
              unpinLabel={t("menu.unpin")}
              chainMenuKey={chainMenuKey}
              onChainMenuChange={setChainMenuKey}
              onEditChain={onEditChain}
              onDuplicateChain={onDuplicateChain}
              onDeleteChain={onDeleteChain}
              onHover={setActiveKey}
              onRun={run}
              onPin={pin}
            />
          </div>
        </div>
      </AutoHeight>
    </MenuShell>
  )
}

function MenuFilterRow({
  scrollerRef,
  glide,
  filter,
  onSelect,
}: {
  scrollerRef: RefObject<HTMLDivElement | null>
  glide: GlideState
  filter: MenuFilter
  onSelect: (filter: MenuFilter) => void
}) {
  const { t } = useTranslation("flow")
  const [edges, setEdges] = useState({ left: false, right: false })

  const syncEdges = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    const next = { left: el.scrollLeft > 2, right: max - el.scrollLeft > 2 }
    setEdges((prev) => (prev.left === next.left && prev.right === next.right ? prev : next))
  }, [scrollerRef])

  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    revealMenuChip(el, filter)
    syncEdges()
    const observer = new ResizeObserver(syncEdges)
    observer.observe(el)
    return () => observer.disconnect()
  }, [filter, scrollerRef, syncEdges])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const onWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return
      const delta = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 16 : event.deltaY
      el.scrollLeft += delta
      event.preventDefault()
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  }, [scrollerRef])

  const mask = filterEdgeMask(edges)

  return (
    <div
      ref={scrollerRef}
      className="it2tv-scrollbar-hidden relative flex flex-nowrap gap-1.5 overflow-x-auto overflow-y-hidden overscroll-x-contain px-3 pt-2.5 pb-1"
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
      onScroll={syncEdges}
    >
      <GlideHighlight glide={glide} className="rounded-full bg-ui-accent-soft" />
      {FILTERS.map((item) => (
        <button
          key={item}
          type="button"
          data-menu-key={item}
          className={cn(
            "relative z-10 h-6.5 shrink-0 rounded-full px-2.5 text-xs whitespace-nowrap transition-colors",
            item === filter
              ? "text-ui-accent-text"
              : "bg-ui-surface-raised text-ui-text-secondary hover:bg-ui-surface-hover hover:text-ui-text-primary",
          )}
          onClick={() => onSelect(item)}
        >
          {t(`menu.filters.${item}`)}
        </button>
      ))}
    </div>
  )
}

function NodeMenuButton({
  icon: Icon,
  label,
  danger,
  onClick,
}: {
  icon: LucideIcon
  label: string
  danger?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-ui-surface-raised",
        danger ? "text-ui-danger" : "text-ui-text-primary",
      )}
      onClick={onClick}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  )
}

function MenuShell({
  x,
  y,
  width,
  maxHeight,
  children,
}: {
  x: number
  y: number
  width: number
  maxHeight: number
  children: ReactNode
}) {
  const left = Math.max(8, Math.min(x, window.innerWidth - width - 8))
  const top = Math.max(8, Math.min(y, window.innerHeight - maxHeight - 8))
  return (
    <div
      className="flow-menu fixed z-50 overflow-hidden rounded-lg border border-ui-border-strong bg-ui-bg-elevated/88 text-ui-text-primary shadow-xl shadow-black/40 backdrop-blur-md dark:bg-zinc-800/80"
      style={{ left, top, width }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  )
}

function MenuRows({
  entries,
  activeKey,
  words,
  pins,
  justPinned,
  pinLabel,
  unpinLabel,
  chainMenuKey,
  onChainMenuChange,
  onEditChain,
  onDuplicateChain,
  onDeleteChain,
  onHover,
  onRun,
  onPin,
}: {
  entries: MenuEntry[]
  activeKey: string | null
  words: readonly string[]
  pins: string[]
  justPinned: string | null
  pinLabel: string
  unpinLabel: string
  chainMenuKey: string | null
  onChainMenuChange: (key: string | null) => void
  onEditChain: (chainId: number) => void
  onDuplicateChain: (chainId: number) => void
  onDeleteChain: (chainId: number) => void
  onHover: (key: string) => void
  onRun: (entry: MenuEntry) => void
  onPin: (key: string) => void
}) {
  const out: ReactNode[] = []
  let section: string | undefined
  entries.forEach((entry, index) => {
    if (entry.section && entry.section !== section) {
      out.push(<MenuSectionLabel key={`section:${entry.section}`} label={entry.section} first={index === 0} />)
      section = entry.section
    }
    const pinKey = entry.pinKey
    const pinned = pinKey != null && pins.includes(pinKey)
    const pin = pinKey == null ? null : { pinned, label: pinned ? unpinLabel : pinLabel, onToggle: () => onPin(pinKey) }
    const chainId = entry.chainId
    out.push(
      <MenuRow
        key={entry.key}
        entry={entry}
        active={entry.key === activeKey}
        enter={justPinned != null && entry.key === pinnedEntryKey(justPinned)}
        words={words}
        pin={chainId == null ? pin : null}
        edit={chainId == null ? (entry.edit ?? null) : null}
        chainMenu={
          chainId == null
            ? null
            : {
                open: chainMenuKey === entry.key,
                pinned,
                onOpenChange: (open) => onChainMenuChange(open ? entry.key : null),
                onPin: pin ? pin.onToggle : undefined,
                onEdit: () => onEditChain(chainId),
                onDuplicate: () => {
                  onChainMenuChange(null)
                  onDuplicateChain(chainId)
                },
                onDelete: () => {
                  onChainMenuChange(null)
                  onDeleteChain(chainId)
                },
              }
        }
        onHover={() => onHover(entry.key)}
        onRun={() => onRun(entry)}
      />,
    )
  })
  return <>{out}</>
}

function MenuSectionLabel({ label, first }: { label: string; first: boolean }) {
  return (
    <div
      className={cn(
        "pb-1 text-[11px] font-medium tracking-wide text-ui-text-muted uppercase",
        first ? "px-2 pt-1.5" : "-mx-1 mt-1 border-t border-ui-border px-3 pt-2",
      )}
    >
      {label}
    </div>
  )
}

type ChainRowMenuProps = {
  open: boolean
  pinned: boolean
  onOpenChange: (open: boolean) => void
  onPin?: () => void
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
}

function MenuRow({
  entry,
  active,
  enter,
  words,
  pin,
  edit,
  chainMenu,
  onHover,
  onRun,
}: {
  entry: MenuEntry
  active: boolean
  enter: boolean
  words: readonly string[]
  pin: { pinned: boolean; label: string; onToggle: () => void } | null
  edit: { label: string; onSelect: () => void } | null
  chainMenu: ChainRowMenuProps | null
  onHover: () => void
  onRun: () => void
}) {
  return (
    <div
      role="option"
      aria-selected={active}
      data-menu-key={entry.key}
      data-active={active}
      className={cn(
        "group/row relative z-10 flex min-h-10 cursor-pointer items-center gap-2.5 rounded-sm px-2 py-0.5",
        enter && "flow-menu-row-in",
      )}
      onPointerMove={() => {
        if (!active) onHover()
      }}
      onClick={onRun}
    >
      <MenuRowLead entry={entry} active={active} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm/5 text-ui-text-primary">
          <HighlightedText text={entry.label} words={words} markClassName="bg-transparent text-ui-link" />
        </span>
        {entry.sub ? <span className="truncate text-xs text-ui-text-muted">{entry.sub}</span> : null}
      </span>
      {chainMenu ? <ChainRowMenu {...chainMenu} revealed={active} /> : null}
      {edit || pin ? (
        <span className="flex shrink-0 items-center gap-0.5">
          {edit ? (
            <button
              type="button"
              aria-label={edit.label}
              className={cn(
                "flex size-6 items-center justify-center rounded-sm text-ui-text-muted transition-[opacity,color,background-color] duration-150 hover:bg-ui-surface-raised hover:text-ui-text-primary focus-visible:opacity-100",
                active ? "opacity-100" : "opacity-0",
              )}
              onClick={(event) => {
                event.stopPropagation()
                edit.onSelect()
              }}
            >
              <Pencil className="size-3.5" />
            </button>
          ) : null}
          {pin ? (
            <button
              type="button"
              aria-label={pin.label}
              aria-pressed={pin.pinned}
              className={cn(
                "group/pin flex size-6 items-center justify-center rounded-sm text-ui-text-muted transition-[opacity,color,background-color] duration-150 hover:bg-ui-surface-raised hover:text-ui-text-primary focus-visible:opacity-100",
                pin.pinned || active ? "opacity-100" : "opacity-0",
              )}
              onClick={(event) => {
                event.stopPropagation()
                pin.onToggle()
              }}
            >
              {pin.pinned ? (
                <>
                  <Pin className="size-3.5 group-hover/pin:hidden" />
                  <PinOff className="hidden size-3.5 group-hover/pin:block" />
                </>
              ) : (
                <Pin className="size-3.5" />
              )}
            </button>
          ) : null}
        </span>
      ) : null}
      {entry.action.type === "open" ? (
        <ChevronRight className="size-3.5 shrink-0 text-ui-text-faint transition-transform duration-150 group-data-[active=true]/row:translate-x-0.5 group-data-[active=true]/row:text-ui-text-muted" />
      ) : null}
    </div>
  )
}

function ChainRowMenu({
  open,
  pinned,
  revealed,
  onOpenChange,
  onPin,
  onEdit,
  onDuplicate,
  onDelete,
}: ChainRowMenuProps & { revealed: boolean }) {
  const { t } = useTranslation("flow")
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [cursor, setCursor] = useState(0)
  const items = [
    ...(onPin
      ? [
          {
            key: "pin",
            label: pinned ? t("menu.unpin") : t("menu.pin"),
            icon: pinned ? PinOff : Pin,
            danger: false,
            close: false,
            run: onPin,
          },
        ]
      : []),
    { key: "edit", label: t("menu.edit"), icon: Pencil, danger: false, close: true, run: onEdit },
    { key: "duplicate", label: t("menu.duplicate"), icon: Copy, danger: false, close: true, run: onDuplicate },
    { key: "delete", label: t("chain.delete"), icon: Trash2, danger: true, close: true, run: onDelete },
  ]
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: "right",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    estimatedMenuWidthPx: 196,
    estimatedMenuHeightPx: 168,
    resolveWidth: () => ({ minWidth: "11.5rem", width: "max-content" }),
    onOutsideClick: () => onOpenChange(false),
  })
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange

  useEffect(() => {
    if (!open) return
    setCursor(0)
  }, [open])

  // Подменю fixed и едет за кнопкой. Закрываем его, когда «⋯» полностью ушла
  // из видимой области родительского меню: вложенный скролл клипует пересечение.
  useEffect(() => {
    if (!open) return
    const button = buttonRef.current
    if (!button) return
    const parent = button.closest(".flow-menu")
    if (!(parent instanceof HTMLElement)) return
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry && !entry.isIntersecting) onOpenChangeRef.current(false)
      },
      { root: parent, threshold: 0 },
    )
    observer.observe(button)
    return () => observer.disconnect()
  }, [open])

  useEffect(() => {
    if (!open || !portalRoot) return
    const frame = window.requestAnimationFrame(() => menuRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [open, portalRoot])

  const choose = (index: number) => {
    const item = items[index]
    if (!item) return
    if (item.close) onOpenChange(false)
    item.run()
  }

  return (
    <div
      ref={rootRef}
      className="shrink-0"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={t("menu.chainActions")}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "flex size-6 items-center justify-center rounded-sm text-ui-text-muted transition-[opacity,color,background-color] duration-150 hover:bg-ui-surface-raised hover:text-ui-text-primary focus-visible:opacity-100",
          open || revealed ? "opacity-100" : "opacity-0",
        )}
        onClick={() => onOpenChange(!open)}
      >
        <EllipsisVertical className="size-3.5" />
      </button>
      {open && portalRoot
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              tabIndex={-1}
              style={menuStyle ?? undefined}
              className="flow-menu overflow-hidden rounded-lg border border-ui-border-strong bg-ui-bg-elevated/88 p-1 text-ui-text-primary shadow-xl shadow-black/40 backdrop-blur-md outline-none dark:bg-zinc-800/80"
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault()
                  event.stopPropagation()
                  const step = event.key === "ArrowDown" ? 1 : -1
                  setCursor((index) => (index + step + items.length) % items.length)
                } else if (event.key === "Enter") {
                  event.preventDefault()
                  event.stopPropagation()
                  choose(cursor)
                } else if (event.key === "Escape") {
                  event.preventDefault()
                  event.stopPropagation()
                  onOpenChange(false)
                  buttonRef.current?.focus()
                }
              }}
            >
              {items.map((item, index) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                      item.danger ? "text-ui-danger" : "text-ui-text-primary",
                      index === cursor ? "bg-ui-surface-raised" : "hover:bg-ui-surface-raised",
                    )}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => choose(index)}
                  >
                    <Icon className="size-3.5" />
                    {item.label}
                  </button>
                )
              })}
            </div>,
            portalRoot,
          )
        : null}
    </div>
  )
}

function MenuRowLead({ entry, active }: { entry: MenuEntry; active: boolean }) {
  const thumb = entry.thumb
  if (thumb?.url) {
    return (
      <img
        src={thumb.url}
        alt=""
        className={cn("shrink-0 rounded-md bg-ui-surface-sunken object-cover", thumb.wide ? "h-6 w-10" : "size-7")}
      />
    )
  }
  if (thumb?.wide) return <span className="h-6 w-10 shrink-0 rounded-md bg-ui-surface-sunken" />
  const Icon = entry.icon
  if (!Icon) return null
  return (
    <span
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-md transition-colors duration-150",
        active ? "bg-ui-accent-soft text-ui-accent-text" : "bg-ui-surface-raised text-ui-text-secondary",
      )}
    >
      <Icon className="size-3.5" />
    </span>
  )
}

function AutoHeight({ children }: { children: ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | null>(null)
  useLayoutEffect(() => {
    const inner = innerRef.current
    if (!inner) return
    const observer = new ResizeObserver(() => setHeight(inner.offsetHeight))
    observer.observe(inner)
    setHeight(inner.offsetHeight)
    return () => observer.disconnect()
  }, [])
  return (
    <div className="flow-menu-height overflow-hidden" style={{ height: height ?? "auto" }}>
      <div ref={innerRef}>{children}</div>
    </div>
  )
}

type GlideRect = { top: number; left: number; width: number; height: number }
type GlideState = { rect: GlideRect | null; visible: boolean; instant: boolean; scope: string }

/** Одна подсветка на список: скользит к активной строке, а не перерисовывается в каждой. */
function useGlide(
  containerRef: RefObject<HTMLDivElement | null>,
  activeKey: string | null,
  measureKey: string,
  scope: string,
): GlideState {
  const [state, setState] = useState<GlideState>({ rect: null, visible: false, instant: true, scope })
  useLayoutEffect(() => {
    const row = activeKey ? findMenuRow(containerRef.current, activeKey) : null
    setState((prev) => {
      if (!row) return prev.visible ? { ...prev, visible: false } : prev
      const rect = { top: row.offsetTop, left: row.offsetLeft, width: row.offsetWidth, height: row.offsetHeight }
      const instant = !prev.visible || prev.scope !== scope
      if (!instant && prev.rect && sameRect(prev.rect, rect)) return prev
      return { rect, visible: true, instant, scope }
    })
  }, [activeKey, containerRef, measureKey, scope])
  return state.scope === scope ? state : { ...state, visible: false, instant: true }
}

function GlideHighlight({ glide, className }: { glide: GlideState; className: string }) {
  const rect = glide.rect
  if (!rect) return null
  return (
    <div
      aria-hidden
      data-instant={glide.instant}
      className={cn("flow-menu-glide pointer-events-none absolute top-0 left-0", className)}
      style={{
        width: rect.width,
        height: rect.height,
        transform: `translate(${rect.left}px, ${rect.top}px)`,
        opacity: glide.visible ? 1 : 0,
      }}
    />
  )
}

function sameRect(a: GlideRect, b: GlideRect): boolean {
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height
}

function findMenuRow(root: HTMLElement | null, key: string): HTMLElement | null {
  if (!root) return null
  return root.querySelector<HTMLElement>(`[data-menu-key="${CSS.escape(key)}"]`)
}

/** Край ряда растворяется, только если за ним ещё есть чипы. */
function filterEdgeMask(edges: { left: boolean; right: boolean }): string | null {
  if (edges.left && edges.right) {
    return "linear-gradient(to right, transparent, #000 16px, #000 calc(100% - 28px), transparent)"
  }
  if (edges.right) return "linear-gradient(to right, #000 calc(100% - 28px), transparent)"
  if (edges.left) return "linear-gradient(to right, transparent, #000 16px)"
  return null
}

function revealMenuChip(scroller: HTMLElement, key: string) {
  const chip = findMenuRow(scroller, key)
  if (!chip) return
  const pad = 20
  const left = chip.offsetLeft - pad
  const right = chip.offsetLeft + chip.offsetWidth + pad
  const viewRight = scroller.scrollLeft + scroller.clientWidth
  let next = scroller.scrollLeft
  if (left < scroller.scrollLeft) next = Math.max(0, left)
  else if (right > viewRight) next = right - scroller.clientWidth
  if (next !== scroller.scrollLeft) scroller.scrollTo({ left: next, behavior: "smooth" })
}

function moveActive(entries: MenuEntry[], activeKey: string | null, delta: number): string | null {
  if (entries.length === 0) return null
  const index = entries.findIndex((entry) => entry.key === activeKey)
  const next = index < 0 ? (delta > 0 ? 0 : entries.length - 1) : (index + delta + entries.length) % entries.length
  return entries[next].key
}

/* ------------------------------ Пункты меню ------------------------------ */

function menuEntries(data: MenuData, view: MenuView, query: string, filter: MenuFilter): MenuEntry[] {
  return connectedEntries(data, unfilteredMenuEntries(data, view, query, filter))
}

function connectedEntries(data: MenuData, entries: MenuEntry[]): MenuEntry[] {
  const drag = data.connectDrag
  if (!drag) return entries
  return entries.filter((entry) => entryConnects(drag, entry, data.chains))
}

function entryConnects(drag: FlowConnectDrag, entry: MenuEntry, chains: FlowChainRow[]): boolean {
  const action = entry.action
  switch (action.type) {
    case "kind":
      return flowFreshKindConnects(drag, action.kind)
    case "open":
      return openViewConnects(drag, action.view)
    case "entity":
      return flowFreshKindConnects(drag, entityViewKind(action.view))
    case "shot":
    case "newShot":
      return flowFreshKindConnects(drag, "shot")
    case "chain": {
      const chain = chains.find((item) => item.id === action.id)
      return chain != null && flowChainConnects(drag, chain)
    }
    case "createBlankChain":
      return false
    default:
      return false
  }
}

function openViewConnects(drag: FlowConnectDrag, view: MenuView): boolean {
  if (view.name === "characters" || view.name === "locations" || view.name === "props") {
    return flowFreshKindConnects(drag, entityViewKind(view.name))
  }
  if (view.name === "root") return true
  return flowFreshKindConnects(drag, "shot")
}

function entityViewKind(view: EntityView): FlowNodeKind {
  if (view === "characters") return "character"
  if (view === "locations") return "location"
  return "prop"
}

function unfilteredMenuEntries(data: MenuData, view: MenuView, query: string, filter: MenuFilter): MenuEntry[] {
  const needle = query.trim().toLowerCase()
  const matches = (entry: MenuEntry) => entry.label.toLowerCase().includes(needle)
  if (view.name !== "root") {
    const list = drillEntries(data, view)
    return needle ? list.filter(matches) : list
  }
  if (filter === "chains") {
    const chains = needle ? chainEntries(data).filter(matches) : chainEntries(data)
    return needle ? chains.slice(0, SEARCH_LIMIT) : chains
  }
  if (!needle) {
    const groups = filter === "all" ? (["gen", "media", "project"] as const) : [filter]
    const grouped = groups.flatMap((group) => groupEntries(data, group, data.t(`menu.sections.${group}`)))
    const chains = filter === "all" ? chainEntries(data) : []
    return filter === "all" ? [...pinnedEntries(data), ...grouped, ...chains] : grouped
  }
  const nodeSection = data.t("menu.sections.nodes")
  const nodeGroups = filter === "all" ? (["gen", "media", "project"] as const) : [filter]
  const nodes = nodeGroups.flatMap((group) => groupEntries(data, group, nodeSection)).filter(matches)
  if (filter === "gen" || filter === "media") return nodes
  const projectSection = data.t("menu.sections.project")
  const found = [...entityEntries(data, projectSection), ...shotEntries(data, projectSection)].filter(matches)
  const chains = filter === "all" ? chainEntries(data).filter(matches) : []
  return [...nodes, ...found, ...chains].slice(0, SEARCH_LIMIT)
}

function chainEntries(data: MenuData): MenuEntry[] {
  const section = data.t("menu.sections.chains")
  const rows = data.chains.map((chain) => chainEntry(data, chain, section))
  if (!data.onCreateBlankChain) return rows
  return [...rows, blankChainEntry(data, section)]
}

function blankChainEntry(data: MenuData, section: string): MenuEntry {
  return {
    key: "create-chain",
    label: data.t("menu.createChain"),
    icon: Plus,
    section,
    action: { type: "createBlankChain" },
  }
}

function chainEntry(data: MenuData, chain: FlowChainRow, section: string): MenuEntry {
  return {
    key: `chain:${chain.id}`,
    label: chain.title,
    sub: data.t("menu.subs.chain"),
    icon: Waypoints,
    section,
    action: { type: "chain", id: chain.id },
    pinKey: `chain:${chain.id}`,
    chainId: chain.id,
  }
}

function kindEntry(t: FlowT, item: KindItem, section?: string): MenuEntry {
  return {
    key: `kind:${item.kind}`,
    label: t(item.label),
    sub: t(item.sub),
    icon: item.icon,
    section,
    action: { type: "kind", kind: item.kind },
    pinKey: `kind:${item.kind}`,
  }
}

function groupEntries(data: MenuData, group: MenuGroup, section: string): MenuEntry[] {
  const { t } = data
  if (group !== "project") {
    return KIND_ITEMS.filter((item) => item.group === group).map((item) => kindEntry(t, item, section))
  }
  return [
    {
      key: "open:shots",
      label: t("menu.shot"),
      sub: data.shots.length > 0 ? t("menu.counts.shots", { count: data.shots.length }) : t("menu.subs.shot"),
      icon: Clapperboard,
      section,
      action: { type: "open", view: { name: "pick" } },
    },
    ...ENTITY_VIEWS.map((view) => {
      const meta = ENTITY_META[view]
      return {
        key: `open:${view}`,
        label: t(meta.label),
        sub: t(meta.count, { count: data.entities[view].length }),
        icon: meta.icon,
        section,
        action: { type: "open", view: { name: view } } as const,
      }
    }),
  ]
}

function entityEntry(data: MenuData, view: EntityView, entity: FlowMenuEntity, section?: string): MenuEntry {
  const meta = ENTITY_META[view]
  return {
    key: entityPinKey(view, entity.id),
    label: entity.name,
    sub: section ? data.t(meta.label) : undefined,
    icon: meta.icon,
    thumb: entity.imageUrl ? { url: entity.imageUrl, wide: false } : undefined,
    section,
    action: { type: "entity", view, id: entity.id },
    pinKey: entityPinKey(view, entity.id),
  }
}

function entityEntries(data: MenuData, section: string): MenuEntry[] {
  return ENTITY_VIEWS.flatMap((view) => data.entities[view].map((entity) => entityEntry(data, view, entity, section)))
}

function shotEntry(data: MenuData, ref: ShotRef, section?: string): MenuEntry {
  const label = shotLabel(ref.episode, ref.scene, ref.shot.number)
  return {
    key: `shot:${ref.shot.id}`,
    label: section ? `${data.t("menu.shot")} ${label}` : label,
    sub: section ? sceneLabel(ref.episode, ref.scene) : undefined,
    thumb: { url: shotMenuThumbUrl(ref.shot), wide: true },
    section,
    action: { type: "shot", id: ref.shot.id },
    pinKey: `shot:${ref.shot.id}`,
  }
}

function shotEntries(data: MenuData, section: string): MenuEntry[] {
  return data.shots.map((ref) => shotEntry(data, ref, section))
}

function pinnedEntries(data: MenuData): MenuEntry[] {
  const section = data.t("menu.sections.pinned")
  return data.pins.flatMap((pin): MenuEntry[] => {
    const entry = pinnedEntry(data, pin, section)
    return entry ? [{ ...entry, key: pinnedEntryKey(pin) }] : []
  })
}

function pinnedEntry(data: MenuData, pin: string, section: string): MenuEntry | null {
  const [type, rawId] = pin.split(":")
  if (type === "kind") {
    const item = KIND_ITEMS.find((candidate) => candidate.kind === rawId)
    return item ? kindEntry(data.t, item, section) : null
  }
  const id = Number(rawId)
  if (type === "chain") {
    const chain = data.chains.find((item) => item.id === id)
    return chain ? chainEntry(data, chain, section) : null
  }
  if (type === "shot") {
    const ref = data.shots.find((candidate) => candidate.shot.id === id)
    return ref ? shotEntry(data, ref, section) : null
  }
  const view = ENTITY_VIEWS.find((candidate) => ENTITY_META[candidate].pin === type)
  const entity = view ? data.entities[view].find((candidate) => candidate.id === id) : undefined
  return view && entity ? entityEntry(data, view, entity, section) : null
}

function drillEntries(data: MenuData, view: MenuView): MenuEntry[] {
  const { t } = data
  if (view.name === "characters" || view.name === "locations" || view.name === "props") {
    return data.entities[view.name].map((entity) => entityEntry(data, view.name, entity))
  }
  if (view.name === "episodes") {
    return data.episodes.map((episode) => ({
      key: `episode:${episode.id}`,
      label: t("menu.episode", { label: episodeLabel(episode) }),
      action: { type: "open", view: { name: "scenes", episodeId: episode.id } },
    }))
  }
  const episode = data.episodes.find(
    (item) => item.id === (view.name === "scenes" || view.name === "shots" ? view.episodeId : -1),
  )
  if (!episode) return []
  if (view.name === "scenes") {
    return episode.scenes.map((scene) => ({
      key: `scene:${scene.id}`,
      label: sceneLabel(episode, scene),
      sub: t("menu.counts.shots", { count: scene.shots.length }),
      action: { type: "open", view: { name: "shots", episodeId: episode.id, sceneId: scene.id } },
    }))
  }
  const scene = view.name === "shots" ? episode.scenes.find((item) => item.id === view.sceneId) : undefined
  if (!scene) return []
  return [
    ...scene.shots.map((shot) => shotEntry(data, { episode, scene, shot })),
    {
      key: `new-shot:${scene.id}`,
      label: t("menu.newShot"),
      icon: Plus,
      action: { type: "newShot", sceneId: scene.id },
    },
  ]
}

function menuNote(
  t: FlowT,
  view: MenuView,
  state: { loading: boolean; loadError: boolean; noScenes: boolean; query: string; empty: boolean; noChains: boolean },
): string | null {
  const shotsView = view.name === "pick" || view.name === "episodes" || view.name === "scenes" || view.name === "shots"
  if (shotsView && state.loading) return t("menu.shotsLoading")
  if (shotsView && state.loadError) return t("menu.shotsLoadFailed")
  if (shotsView && state.noScenes) return t("menu.noScenes")
  if (!state.empty) return null
  if (state.query.trim()) return t("menu.noResults", { query: state.query.trim() })
  if (state.noChains) return t("menu.noChains")
  if (view.name === "characters" || view.name === "locations" || view.name === "props") {
    return t(ENTITY_META[view.name].empty)
  }
  return null
}

function viewTitle(t: FlowT, view: MenuView, episodes: CanvasShotTreeEpisode[]): string {
  if (view.name === "characters" || view.name === "locations" || view.name === "props") {
    return t(ENTITY_META[view.name].label)
  }
  const episode = episodes.find(
    (item) => item.id === (view.name === "scenes" || view.name === "shots" ? view.episodeId : -1),
  )
  if (view.name === "shots" && episode) {
    const scene = episode.scenes.find((item) => item.id === view.sceneId)
    if (scene) return sceneLabel(episode, scene)
  }
  if (view.name === "scenes" && episode && episodes.length > 1) {
    return t("menu.episode", { label: episodeLabel(episode) })
  }
  return t("menu.shot")
}

function resolveView(view: MenuView, episodes: CanvasShotTreeEpisode[]): MenuView {
  if (view.name !== "pick" || episodes.length === 0) return view
  if (episodes.length > 1) return { name: "episodes" }
  return { name: "scenes", episodeId: episodes[0].id }
}

function menuViewKey(view: MenuView): string {
  if (view.name === "scenes") return `scenes:${view.episodeId}`
  if (view.name === "shots") return `shots:${view.sceneId}`
  return view.name
}

function flattenShots(episodes: CanvasShotTreeEpisode[]): ShotRef[] {
  return episodes.flatMap((episode) =>
    episode.scenes.flatMap((scene) => scene.shots.map((shot) => ({ episode, scene, shot }))),
  )
}

function entityPinKey(view: EntityView, id: number): string {
  return `${ENTITY_META[view].pin}:${id}`
}

function pinnedEntryKey(pin: string): string {
  return `pinned:${pin}`
}

function propMenuThumbUrl(prop: PropRow): string | null {
  return prop.front_view_thumb_url?.trim() || prop.thumb_url?.trim() || prop.reference_sheet_url?.trim() || null
}

function shotMenuThumbUrl(shot: CanvasShotTreeShot): string | null {
  const storyboard = shot.storyboard_url?.trim()
  if (storyboard) return storyboard
  const firstFrame = shot.video_first_frame_url?.trim()
  return firstFrame || null
}

function episodeLabel(episode: CanvasShotTreeEpisode): string {
  return episode.title ? `${episode.number} · ${episode.title}` : String(episode.number)
}

function sceneLabel(episode: CanvasShotTreeEpisode, scene: CanvasShotTreeScene): string {
  const number = `${episode.number}.${scene.number}`
  return scene.title ? `${number} · ${scene.title}` : number
}

function shotLabel(episode: CanvasShotTreeEpisode, scene: CanvasShotTreeScene, number: string): string {
  return `${episode.number}.${scene.number}.${number}`
}
